import type { UpdateOrganizationRequest } from '@ruta/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { useCallback, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  WarningCircle,
  CreditCard,
  CircleNotch,
  Shield,
  ListBullets,
  ChartBar,
  DownloadSimple,
  Receipt,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardLabel } from '@/components/ui/card';
import { PlatformBillingChoice } from './PlatformBillingChoice';
import { SettingsCard, SETTINGS_ICON_TILE } from '@/components/ui/settings-card';
import { useToast } from '@/components/ui/use-toast';
import { formatCurrency, formatWholeDollars, parseDate } from '@/lib/utils';
import { useOrganization, usePlatformInvoices } from '@/hooks/useQueries';
import { queryKeys } from '@/lib/queryKeys';
import { usePermission } from '@/hooks/usePermission';
import { platformApi } from '@/lib/api/routes/platform';
import { updateOrganization } from '@/lib/api/routes/organizations';
import JustifiPaymentForm, { JustifiPaymentFormHandle } from '@/components/JustifiPaymentForm';
import { downloadPlatformInvoicePdf } from '@/components/settings/platformInvoicePdf';
import {
  Permission,
  PLATFORM_AI_INCLUDED_COPY,
  PLATFORM_AI_OVERAGE_ENABLED,
  PLATFORM_OVERAGE_COPY,
  SubscriptionStatus,
  SubscriptionTier,
  platformDealName,
  presentPlatformInvoice,
  resolvePlatformBillingPlan,
  platformBillingInterval,
  platformSubscriptionPrice,
  isFirstChargeAfterPlatformTrial,
  isOnPlatformTrial,
  isLockedAfterPlatformTrial,
  isEligibleForPlatformTrial,
  PLATFORM_TRIAL_DAYS,
} from '@ruta/shared';
import type { PlatformInvoice } from '@ruta/shared';

const STATUS_BADGE: Record<
  string,
  { label: string; variant: 'default' | 'success' | 'warning' | 'secondary' | 'outline' }
> = {
  [SubscriptionStatus.ACTIVE]: { label: 'Active', variant: 'success' },
  [SubscriptionStatus.PAYMENT_PENDING]: { label: 'Payment pending', variant: 'warning' },
  [SubscriptionStatus.INACTIVE]: { label: 'Inactive', variant: 'outline' },
  [SubscriptionStatus.CANCELLED]: { label: 'Cancelled', variant: 'secondary' },
};

const CARD_BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'Amex',
  'american express': 'Amex',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

function cardBrandLabel(brand?: string): string {
  if (!brand) return 'Card';
  const key = brand.toLowerCase();
  return CARD_BRAND_LABELS[key] ?? brand.charAt(0).toUpperCase() + brand.slice(1);
}

function invoicePeriodLabel(invoice: PlatformInvoice): string | null {
  if (invoice.billingType === 'activation' && !invoice.periodStart) {
    return 'Activation';
  }
  if (invoice.periodStart && invoice.periodEnd) {
    return `${format(parseDate(invoice.periodStart), 'MMM d')} – ${format(parseDate(invoice.periodEnd), 'MMM d')}`;
  }
  if (invoice.billingType === 'activation') return 'Activation';
  return null;
}

export function SubscriptionTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission(Permission.COMPANY_MANAGE);

  const { data: orgData, isLoading } = useOrganization();
  const { data: invoicesData, isLoading: invoicesLoading } = usePlatformInvoices();
  const org = orgData?.organization;
  const usage = orgData?.usage;
  const billingPrefill = org?.name ? { name: org.name } : undefined;
  const invoices = invoicesData?.invoices ?? [];
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownloadInvoice = useCallback(async (invoice: PlatformInvoice) => {
    setDownloadingId(invoice.id);
    try {
      await downloadPlatformInvoicePdf(invoice, {
        companyLogoUrl: org?.logoUrl ?? org?.emailLogoUrl ?? null,
      });
    } catch (err) {
      console.error('Failed to generate invoice PDF', err);
      toast({
        title: 'Could not download invoice',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setDownloadingId(null);
    }
  }, [org?.logoUrl, org?.emailLogoUrl, toast]);

  const tier: SubscriptionTier | undefined = org?.subscriptionTier;
  const status: SubscriptionStatus | undefined = org?.subscriptionStatus;
  const isActive = status === SubscriptionStatus.ACTIVE;
  const hasRealPaymentMethod = !!org?.paymentMethodId && org.paymentMethodId !== 'skipped';
  const isSubscribed = isActive && hasRealPaymentMethod;
  const isEnterprise = tier === SubscriptionTier.ENTERPRISE;
  const onTrial = !!org && isOnPlatformTrial(org);
  // After the trial ends, payment activates the selected monthly or annual interval.
  const trialEnded = !!org && isLockedAfterPlatformTrial(org);
  // Saving a card starts the free trial instead of charging (never-activated companies).
  const startsTrial = !!org && !isEnterprise && isEligibleForPlatformTrial(org);

  const deal = resolvePlatformBillingPlan(tier, org?.billingPlan);
  const dealName = platformDealName(tier);
  // Show the full amount for the next selected interval, never a monthly equivalent.
  const interval = platformBillingInterval(org ?? {});
  const dealPrice = deal ? platformSubscriptionPrice(deal, interval) : usage?.listPrice ?? null;
  const priceLabel = dealPrice != null ? formatWholeDollars(dealPrice) : null;
  const cancelledTrial = !!org && isFirstChargeAfterPlatformTrial(org) && status === SubscriptionStatus.CANCELLED && new Date(org.trialEndsAt!) > new Date();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const planMutation = useMutation({
    mutationFn: (change: Pick<UpdateOrganizationRequest, 'billingInterval' | 'platformSubscriptionAction'>) => updateOrganization(change),
    onSuccess: () => {
      setPlanError(null);
      setConfirmCancel(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.current() });
    },
    onError: (error: unknown) => setPlanError(error instanceof Error ? error.message : 'Could not update your plan. Please try again.'),
  });

  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState<'activate' | 'update'>('activate');
  const [accountId, setAccountId] = useState('');
  const [webComponentToken, setWebComponentToken] = useState('');
  const [isPreparing, setIsPreparing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const justifiFormRef = useRef<JustifiPaymentFormHandle>(null);

  const openForm = async (mode: 'activate' | 'update') => {
    setFormMode(mode);
    setIsPreparing(true);
    try {
      const config = await platformApi.getConfig();
      setAccountId(config.payment.subaccountId);
      setWebComponentToken(config.payment.webComponentToken);
      setShowForm(true);
    } catch (error: unknown) {
      toast({
        title: 'Could not start payment setup',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsPreparing(false);
    }
  };

  const mutation = useMutation({
    mutationFn: async (card: { paymentMethodId: string; brand?: string; last4?: string }) => {
      if (!tier) throw new Error('This organization has no plan selected.');
      const { organization } = await updateOrganization({
        paymentMethodId: card.paymentMethodId,
        paymentMethodBrand: card.brand,
        paymentMethodLast4: card.last4,
        subscriptionTier: tier,
        billingInterval: interval,
      });
      return organization;
    },
    onSuccess: (organization) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.current() });
      setShowForm(false);
      setIsSubmitting(false);

      if (formMode === 'update') {
        toast({ title: 'Card updated', description: 'Your payment method has been replaced.' });
        return;
      }

      if (startsTrial && organization?.trialEndsAt) {
        toast({
          title: 'Free trial started',
          description: `Nothing was charged. Your first bill is ${format(parseDate(organization.trialEndsAt), 'MMM d')}.`,
        });
      } else if (organization?.subscriptionStatus === SubscriptionStatus.ACTIVE) {
        toast({
          title: 'Subscription active',
          description: "Your plan is active. You're all set.",
        });
      } else {
        toast({
          title: "Card saved, but the payment didn't go through",
          description: 'We saved the card but the charge was declined. Try a different card.',
          variant: 'destructive',
        });
      }
    },
    onError: (error: unknown) => {
      setIsSubmitting(false);
      toast({
        title: 'Payment failed',
        description: error instanceof Error ? error.message : 'Could not process the payment.',
        variant: 'destructive',
      });
    },
  });

  const handleConfirm = async () => {
    if (!justifiFormRef.current) return;
    setIsSubmitting(true);
    try {
      await justifiFormRef.current.submit();
    } catch (error: unknown) {
      setIsSubmitting(false);
      toast({
        title: 'Payment error',
        description: error instanceof Error ? error.message : 'Failed to read card details.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch className="h-8 w-8 animate-spin text-stone-400" />
      </div>
    );
  }

  if (isEnterprise) {
    return (
      <SettingsCard
        label="Enterprise"
        subtitle="Billed under a custom agreement. Contact your account manager to make changes."
        icon={Shield}
      />
    );
  }

  const badge = onTrial
    ? { label: 'Free trial', variant: 'success' as const }
    : trialEnded
      ? { label: 'Trial ended', variant: 'warning' as const }
      : status
        ? STATUS_BADGE[status]
        : undefined;
  const nextBill = isSubscribed && !onTrial ? org?.nextBillingDate : undefined;
  const periodLabel = usage
    ? `${format(parseDate(usage.periodStart), 'MMM d')} – ${format(parseDate(usage.periodEnd), 'MMM d')}`
    : null;

  const presentedInvoices = invoices.map(presentPlatformInvoice);

  return (
    <div className="w-full space-y-5">
      {/* Full-width plan strip — same anatomy as Billing's Ruta Pay row */}
      <Card>
        <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <div className={SETTINGS_ICON_TILE}>
              <ListBullets className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div data-slot="card-header" className="flex flex-wrap items-center gap-2">
                <CardLabel>Plan</CardLabel>
                {badge && (
                  <Badge variant={badge.variant} dot>
                    {badge.label}
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-xl font-semibold tracking-tight text-stone-900">
                {dealName ?? 'No plan'}
              </p>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-stone-500">
                {nextBill && (
                  <span>Next bill {format(parseDate(nextBill), 'MMM d, yyyy')}</span>
                )}
                {onTrial && org?.trialEndsAt && (
                  <span>
                    Free until {format(parseDate(org.trialEndsAt), 'MMM d, yyyy')}
                  </span>
                )}
                {isSubscribed && (org?.paymentMethodLast4 || org?.paymentMethodBrand) && (
                  <span className="inline-flex items-center gap-1.5 font-medium text-stone-700">
                    <CreditCard className="h-4 w-4 text-stone-400" />
                    {cardBrandLabel(org?.paymentMethodBrand)}
                    {org?.paymentMethodLast4 && (
                      <>
                        <span className="text-stone-400">••••</span>
                        <span className="tabular-nums">{org.paymentMethodLast4}</span>
                      </>
                    )}
                  </span>
                )}
                {canManage && isSubscribed && !showForm && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs font-medium text-primary hover:text-primary"
                    onClick={() => openForm('update')}
                    disabled={isPreparing}
                  >
                    {isPreparing ? <CircleNotch className="h-3.5 w-3.5 animate-spin" /> : 'Change'}
                  </Button>
                )}
              </div>
              {!canManage && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-stone-400">
                  <WarningCircle className="h-3.5 w-3.5" />
                  Only administrators can manage billing
                </p>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3 pl-12 sm:pl-0">
            {priceLabel && (
              <div className="sm:text-right">
                <p className="text-2xl font-semibold tabular-nums tracking-tight text-stone-900">
                  {priceLabel}/{interval === 'annual' ? 'yr' : 'mo'}
                </p>

              </div>
            )}
            {canManage && !isSubscribed && !showForm && !cancelledTrial && (
              <Button
                size="sm"
                onClick={() => openForm('activate')}
                disabled={isPreparing || !tier}
              >
                {isPreparing ? (
                  <CircleNotch className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="mr-2 h-4 w-4" />
                )}
                {trialEnded ? 'Pay to unlock' : startsTrial ? 'Start free trial' : priceLabel ? `Pay ${priceLabel} to Activate` : 'Add Payment Method'}
              </Button>
            )}
          </div>
        </div>
        {deal && (
          <div className="space-y-3 border-t border-border p-4">
            <p className="text-sm font-medium text-stone-700">Billing schedule</p>
            <div className="max-w-sm">
              <PlatformBillingChoice value={interval} monthlyPrice={deal.basePrice} annualPrice={deal.annualPrice} onChange={(billingInterval) => planMutation.mutate({ billingInterval })} disabled={!canManage || planMutation.isPending || isSubmitting || showForm} />
            </div>
            <p className="text-sm text-stone-500">
              {onTrial || cancelledTrial ? 'Your original trial end date stays the same. Nothing is charged today.' : 'Changes take effect at your next bill. Your current paid period stays the same.'}
            </p>
            {planError && <Alert variant="destructive"><AlertDescription>{planError}</AlertDescription></Alert>}
            {canManage && onTrial && !confirmCancel && (
              <Button variant="ghost" size="sm" disabled={planMutation.isPending} onClick={() => setConfirmCancel(true)}>Cancel free trial</Button>
            )}
            <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel your free trial?</AlertDialogTitle>
                  <AlertDialogDescription>You will not be charged. You can keep using Ruta until your trial ends.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={planMutation.isPending}>Keep trial</AlertDialogCancel>
                  <AlertDialogAction disabled={planMutation.isPending} onClick={(event) => { event.preventDefault(); planMutation.mutate({ platformSubscriptionAction: 'cancel_trial' }); }}>Confirm cancellation</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {cancelledTrial && (
              <div className="space-y-2">
                <p className="text-sm text-stone-700">Trial cancelled. You will not be charged. Access continues until {format(parseDate(org!.trialEndsAt!), 'MMM d, yyyy')}.</p>
                {canManage && <Button variant="secondary" size="sm" disabled={planMutation.isPending} onClick={() => planMutation.mutate({ platformSubscriptionAction: 'resume_trial' })}>Resume trial</Button>}
              </div>
            )}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
        {usage && (
          <SettingsCard
            label="Usage"
            subtitle="Included in your plan"
            icon={ChartBar}
          >
            <div className="space-y-4">
              <p className="text-sm text-stone-700">{usage.texts.used.toLocaleString()} messages sent this period</p>
            </div>
            <p className="mt-4 text-sm text-stone-500">
              {usage.visits.toLocaleString()} {usage.visits === 1 ? 'visit' : 'visits'} this period
            </p>
            <p className="mt-2 text-sm text-stone-500">{PLATFORM_OVERAGE_COPY}</p>
            {!PLATFORM_AI_OVERAGE_ENABLED && (
              <p className="mt-2 text-sm text-stone-500">{PLATFORM_AI_INCLUDED_COPY}</p>
            )}
          </SettingsCard>
        )}

        <SettingsCard
          label="Billing"
          subtitle={periodLabel ?? 'What JustiFi charged for software'}
          icon={Receipt}
        >
          {usage && (
            <div className="mb-4 space-y-2">
              <div className="flex flex-wrap items-baseline gap-2">
                <p className="text-2xl font-semibold tabular-nums tracking-tight text-stone-900">
                  {formatCurrency(usage.estimatedTotal)}
                </p>
                <p className="text-[11px] font-medium uppercase tracking-wider text-stone-400">
                  Estimated
                </p>
              </div>

            </div>
          )}
          {invoicesLoading ? (
            <div className="flex items-center justify-center py-8 text-stone-400">
              <CircleNotch className="h-5 w-5 animate-spin" />
            </div>
          ) : presentedInvoices.length === 0 ? (
            <p className="py-2 text-sm text-stone-400">
              No invoices yet. Charges appear here after your first successful payment.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {presentedInvoices.map((row) => {
                const period = invoicePeriodLabel(row.invoice);
                return (
                  <li
                    key={row.id}
                    className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-stone-800">
                        {format(new Date(row.billedAt), 'MMM d, yyyy')}
                      </p>
                      {period && (
                        <p className="mt-0.5 text-xs text-stone-400">{period}</p>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span className="text-sm text-stone-700">{row.planName}</span>
                        {row.isLegacyDeal && (
                          <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
                            Legacy
                          </Badge>
                        )}
                      </div>
                      {row.openReason && (
                        <p className="mt-1 text-xs text-stone-500">{row.openReason}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-semibold tabular-nums text-stone-900">
                        {formatCurrency(row.amount)}
                      </span>
                      <Badge
                        variant={row.status === 'paid' ? 'success' : 'warning'}
                        className="text-[10px] uppercase tracking-wide"
                      >
                        {row.status === 'paid' ? 'Paid' : 'Open'}
                      </Badge>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="h-8"
                        onClick={() => handleDownloadInvoice(row.invoice)}
                        disabled={downloadingId === row.id}
                      >
                        {downloadingId === row.id ? (
                          <CircleNotch className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <DownloadSimple className="h-3.5 w-3.5 mr-1.5" />
                        )}
                        PDF
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </SettingsCard>
      </div>

      {showForm && (
        <SettingsCard
          label={formMode === 'update' ? 'Update card' : 'Payment details'}
          subtitle={
            formMode === 'update'
              ? 'Replaces the card on file for future billing'
              : startsTrial
                ? `Free for ${PLATFORM_TRIAL_DAYS} days — nothing is charged today`
                : priceLabel
                ? `Charged ${priceLabel} today, then ${interval === 'annual' ? 'yearly' : 'monthly'}`
                : 'Enter your card to activate'
          }
          icon={CreditCard}
        >
          <div className="space-y-4">
            {accountId && webComponentToken && (
              <JustifiPaymentForm
                ref={justifiFormRef}
                accountId={accountId}
                authToken={webComponentToken}
                billingPrefill={billingPrefill}
                onSuccess={(data) =>
                  mutation.mutate({
                    paymentMethodId: data.paymentMethodId,
                    brand: data.brand,
                    last4: data.last4,
                  })
                }
                onError={(error) => {
                  setIsSubmitting(false);
                  toast({ title: 'Payment error', description: error, variant: 'destructive' });
                }}
              />
            )}

            <div className="flex items-center justify-between border-t border-border pt-4">
              <span className="flex items-center gap-1.5 text-xs text-stone-400">
                <Shield className="h-3.5 w-3.5" />
                Secured by Justifi
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowForm(false);
                    setAccountId('');
                    setWebComponentToken('');
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleConfirm}
                  disabled={isSubmitting}
                  className="min-w-[100px]"
                >
                  {isSubmitting ? (
                    <>
                      <CircleNotch className="mr-2 h-4 w-4 animate-spin" />
                      Processing…
                    </>
                  ) : formMode === 'update' ? (
                    'Save card'
                  ) : startsTrial ? (
                    'Start free trial'
                  ) : trialEnded ? (
                    'Pay'
                  ) : priceLabel ? (
                    `Pay ${priceLabel}`
                  ) : (
                    'Subscribe'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </SettingsCard>
      )}
    </div>
  );
}
